import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Button, Drawer, message, Table } from 'antd';
import { connect } from 'react-redux';
import { ColumnsType } from 'antd/es/table';
import { useParams } from 'react-router-dom';
import { cloneDeep, debounce } from 'lodash';
import IconFont from '@/components/IconFont';
import AdditionalRecordMobile, {
  IadditionalRecordMobileRef,
} from '@/pages/order/component/AdditionalRecordMobile';
import { TenantGlobalConfig } from '@/components/Intl/IntlContext/helper';
import { formatAreaMobileStr } from '@/components/BaseMobileInput/helper';
import { getIsAdult, renderIntlLabel } from '@/utils';
import { parseFen2Currency, parseFen2Yuan } from '@/utils/number';
import { HistoryParams } from '@/utils/type';
import { getTimeByUtcOffset } from '@/utils/intl';
import {
  BILL_STATE_MAP,
  BILL_TYPE_MAP,
  SubOrderStatus,
  SUB_ORDER_STATUS_MAP,
  DATE_TIME_FORMAT,
  ContractType,
} from '@/config';
import {
  getSplitRoomDetail,
  initBillList,
  orderUpdateMember,
  submitSplitRoom,
} from '@/redux/actions/order';
import styles from './index.module.scss';

const billColumns: ColumnsType<any> = [
  {
    title: renderIntlLabel('order_roomorder_room_number'),
    dataIndex: 'roomNo',
    render: (v) => v || '-',
  },
  {
    title: renderIntlLabel('order_frontDesk_label_bill_enter_account_type'),
    dataIndex: 'postType',
    render: (type) => (BILL_TYPE_MAP[type] ? BILL_TYPE_MAP[type] : '-'),
  },
  {
    title: renderIntlLabel('order_frontDesk_label_subjectName'),
    dataIndex: 'subjectCodeName',
    render: (v) => v || '-',
  },
  {
    title: renderIntlLabel('order_frontDesk_label_payDetail'),
    dataIndex: 'detail',
    render: (v) => v || '-',
  },
  {
    title: renderIntlLabel('order_action_pay'),
    dataIndex: 'amount',
    render: (val, record) => {
      // 付款和退款，退款为负
      if ([1, 4].includes(record.postType)) {
        return (
          <>
            <span>
              {record.postType === 4 ? '-' : ''}
              {parseFen2Yuan(val || 0)}
            </span>
          </>
        );
      }
      return '-';
    },
    align: 'right',
  },
  {
    title: renderIntlLabel('order_action_consume'),
    dataIndex: 'amount',
    render: (val, record) => {
      // 消费、冲调、优惠，冲调和优惠为负
      if ([0, 2, 3].includes(record.postType)) {
        return (
          <>
            <span>
              {[2, 3].includes(record.postType) ? '-' : ''}
              {parseFen2Yuan(val || 0)}
            </span>
          </>
        );
      }
      return '-';
    },
    align: 'right',
  },
  {
    title: renderIntlLabel('order_time'),
    dataIndex: 'createTime',
    render: (val) => getTimeByUtcOffset(val, DATE_TIME_FORMAT),
    width: 180,
  },
  {
    title: renderIntlLabel('order_frontDesk_createBy'),
    dataIndex: 'createBy',
    render: (v) => v || '-',
  },
  {
    title: renderIntlLabel('order_roomorder_settle_financial_status'),
    dataIndex: 'status',
    render: (status) => (BILL_STATE_MAP[status] ? BILL_STATE_MAP[status] : '-'),
  },
  {
    title: renderIntlLabel('order_frontDesk_label_bill_remark'),
    dataIndex: 'remark',
  },
];

const SplitRoom = ({
  memberLevelMap,
  afterOpenChange,
  myRef,
  recNoList,
  recordList,
  splitRoomTypeList,
  billList,
  getSplitRoomDetailFn,
  submitSplitRoomFn,
  initBillListFn,
  orderUpdateMemberFn,
  splitRoomDetailLoading,
  billListLoading,
  submitSplitRoomLoading,
  updateMemberLoading,
}) => {
  const { resvNo } = useParams<HistoryParams>();
  const [visible, setVisible] = useState<boolean>(false);
  const [selectedMemberId, setSelectedMemberId] = useState();
  // const [selectedBills, setSelectedBills] = useState<Array<any>>();
  const [selectedBillIds, setSelectedBillIds] = useState<Array<any>>();
  const modalRef = useRef<IadditionalRecordMobileRef>();

  // 计算每个房间的跨几行（有多少人）
  const splitRoomDataSource = useMemo(() => {
    if (Array.isArray(splitRoomTypeList)) {
      const sourceList = [...splitRoomTypeList];
      // 按房间号分组
      sourceList.sort((a, b) => {
        if (a.roomId < b.roomId) {
          return -1;
        }
        if (a.roomId > b.roomId) {
          return 1;
        }
        return 0;
      });
      const tempList = [];
      const tempRoomObj = {};
      sourceList.forEach((item) => {
        if (!tempRoomObj[item.roomId]) {
          tempRoomObj[item.roomId] = 1;
          tempList.push({ ...item, rowSpan: tempRoomObj[item.roomId] });
        } else {
          tempRoomObj[item.roomId] += 1;
          // 同一个房间更新第一个房间rowSpan:相同房间数
          // 非第一个房间rowSpan:0，末尾添加item，
          const firstIndex = tempList.findIndex((temp) => item.roomId === temp.roomId);
          const firstItem = tempList[firstIndex];
          const reverseTempList = [...tempList];
          reverseTempList.reverse();
          const lastIndex = reverseTempList.findIndex((temp) => item.roomId === temp.roomId);
          tempList.splice(firstIndex, 1, { ...firstItem, rowSpan: tempRoomObj[item.roomId] });
          // 后一位
          tempList.splice(lastIndex + 1, 0, { ...item, rowSpan: 0 });
        }
      });
      return tempList;
    }
    return [];
  }, [splitRoomTypeList]);
  const selectedMemberItem = useMemo(() => {
    if (
      selectedMemberId !== undefined &&
      selectedMemberId !== null &&
      Array.isArray(splitRoomDataSource)
    ) {
      return splitRoomDataSource.find((item) => selectedMemberId === item.id) || {};
    }
    return {};
  }, [selectedMemberId, splitRoomDataSource]);

  const reset = () => {
    setSelectedMemberId(undefined);
    // setSelectedBills([]);
    setSelectedBillIds([]);
  };
  const onOpen = () => {
    setVisible(true);
  };
  const onClose = () => {
    setVisible(false);
    reset();
  };

  const handleConfirm = debounce(() => {
    if (selectedMemberId === undefined || selectedMemberId === null) {
      message.warning(renderIntlLabel('order_detail_msg_please_select_new_house_owner'));
      return;
    }

    const { mobile, email, certNo } = selectedMemberItem;
    if (!getIsAdult(certNo)) {
      message.error(renderIntlLabel('order_detail_msg_minor_cannot_be_homeowners'));
      return;
    }
    // 设为房主，不存在常用联系方式
    const defaultType = TenantGlobalConfig.getSystemConfig().contractType;
    if (
      (defaultType === ContractType.MOBILE && !mobile) ||
      (defaultType === ContractType.EMAIL && !email)
    ) {
      modalRef.current.openModal({
        record: selectedMemberItem,
        callback: (values, _) => {
          const { id, name } = _;
          orderUpdateMemberFn(
            {
              id,
              name,
              ...values,
            },
            true,
          ).then(() => {
            modalRef.current.closeModal();
            getSplitRoomDetailFn({ recNos: recNoList });
          });
        },
      });
      return;
    }
    submitSplitRoomFn({
      resvNo,
      recNos: recNoList,
      itemNos: selectedBillIds,
      roomMasterId: selectedMemberId,
      billNum: (selectedBillIds || []).length,
    }).then(() => {
      setVisible(false);
      reset();
      afterOpenChange?.();
    });
  }, 200);
  const handleCancel = () => {
    setVisible(false);
    reset();
  };
  const handleBecomeOwner = (id) => {
    setSelectedMemberId(id);
  };

  // 初始化默认选中房主
  useEffect(() => {
    if (Array.isArray(splitRoomDataSource)) {
      const { id } =
        splitRoomDataSource.find((item) => item.status === SubOrderStatus.CheckIn) || {};
      setSelectedMemberId(id);
    }
  }, [splitRoomDataSource]);

  useEffect(() => {
    if (
      Array.isArray(recordList) &&
      recordList.length &&
      Array.isArray(billList) &&
      billList.length
    ) {
      const roomIdList = recordList.map((item) => item.roomId);
      const keyList = billList
        .filter((item) => roomIdList.includes(item.roomId))
        ?.map((item) => item.no);
      setSelectedBillIds(keyList);
    }
  }, [recordList, billList]);

  useEffect(() => {
    if (visible && Array.isArray(recNoList)) {
      getSplitRoomDetailFn({ recNos: recNoList });
      initBillListFn({ resvNo, status: 0 });
    }
  }, [visible, recNoList]);

  useImperativeHandle(
    myRef,
    () => ({
      onClose,
      onOpen,
    }),
    [],
  );

  const rowSelection = {
    fixed: true,
    selectedRowKeys: selectedBillIds,
    onChange: (rowKeys, rows) => {
      let tmpRowKeys = cloneDeep(rowKeys);
      let tmpRows = cloneDeep(rows);

      billList.forEach((bill) => {
        // auto select sub bills
        if (tmpRowKeys.includes(bill.no) && bill.subs && bill.subs.length > 0) {
          bill.subs.forEach((sub) => {
            if (!tmpRowKeys.includes(sub.no)) {
              tmpRowKeys = tmpRowKeys.concat(sub.no);
              tmpRows = tmpRows.concat(sub);
            }
          });
        }
        // remove parent bills
        if (
          bill.reverseItemNo &&
          tmpRowKeys.includes(bill.no) &&
          !tmpRowKeys.includes(bill.reverseItemNo)
        ) {
          if (tmpRowKeys.includes(bill.no)) {
            tmpRowKeys.splice(tmpRowKeys.indexOf(bill.no), 1);
            tmpRows = tmpRows.filter((row) => row.no !== bill.no);
          }
        }
      });
      setSelectedBillIds(tmpRowKeys);
      // setSelectedBills(tmpRows);
    },
    getCheckboxProps: (record) => ({
      // 冲调、优惠、退款账目不可选
      disabled: record.postType === 2 || record.postType === 3 || record.postType === 4,
    }),
  };

  const columns: ColumnsType<any> = [
    {
      title: renderIntlLabel('order_roomorder_room_type'),
      dataIndex: 'roomTypeName',
      onCell: (record) => ({
        rowSpan: record.rowSpan,
      }),
    },
    {
      title: renderIntlLabel('order_roomorder_room_number'),
      dataIndex: 'roomNo',
      render: (val, record) =>
        (record.status === SubOrderStatus.Reservation ||
          record.status === SubOrderStatus.CancelReservation) &&
        !val
          ? renderIntlLabel('order_wait_arrange')
          : val,
      onCell: (record) => ({
        rowSpan: record.rowSpan,
      }),
    },
    {
      title: renderIntlLabel('order_status'),
      dataIndex: 'status',
      render: (val) => SUB_ORDER_STATUS_MAP[val],
      onCell: (record) => ({
        rowSpan: record.rowSpan,
      }),
    },
    {
      title: renderIntlLabel('order_roomorder_checkin_person'),
      dataIndex: 'name',
      render: (val, record) => (record.status === SubOrderStatus.CheckIn ? val : ''),
    },
    {
      title: renderIntlLabel('order_roomorder_cert_type'),
      dataIndex: 'certType',
      render: (val) => TenantGlobalConfig.getSystemConfig().certTypesMap?.[val],
    },
    {
      title: renderIntlLabel('order_roomorder_cert_no'),
      dataIndex: 'certNo',
      width: 200,
    },
    {
      title: renderIntlLabel('order_roomorder_mobile'),
      dataIndex: 'mobile',
      render: (val) => formatAreaMobileStr(val),
    },
    {
      title: renderIntlLabel('help_store_address_type_email'),
      dataIndex: 'email',
      render: (val) => val || '—',
    },
    {
      title: renderIntlLabel('order_roomorder_member_level'),
      dataIndex: 'memberLevel',
      render: (val) => memberLevelMap[val]?.name,
    },
    {
      title: renderIntlLabel('order_detail_remainingBalance'),
      dataIndex: 'remainingBalance',
      render: (val) => parseFen2Currency(val || 0),
      align: 'right',
    },
    {
      title: renderIntlLabel('order_detail_new_homeowner'),
      render: (_, record) => {
        if (record.status === SubOrderStatus.CheckIn) {
          if (selectedMemberId) {
            return selectedMemberId === record.id ? (
              <div className={styles.newOwner}>{renderIntlLabel('order_detail_new_homeowner')}</div>
            ) : (
              <>
                <div onClick={() => handleBecomeOwner(record.id)}>
                  <IconFont style={{ color: '#3F79FF', marginRight: '4px' }} type="icon-zhiding" />
                  <Button type="link">{renderIntlLabel('order_action_set_house_owner')}</Button>
                </div>
              </>
            );
          }
        }
        return null;
      },
      fixed: 'right',
    },
  ];

  return (
    <>
      <Drawer
        className={styles.container}
        title={renderIntlLabel('order_action_split_unite_room')}
        placement="right"
        width="60%"
        footer={
          <>
            <Button
              type="primary"
              loading={submitSplitRoomLoading || splitRoomDetailLoading || billListLoading}
              onClick={handleConfirm}
            >
              {renderIntlLabel('order_action_split_into_new_order')}
            </Button>
            <Button onClick={handleCancel}>
              {renderIntlLabel('order_frontDesk_action_btn_cancel')}
            </Button>
          </>
        }
        maskClosable={false}
        visible={visible}
        onClose={onClose}
      >
        <AdditionalRecordMobile ref={modalRef} submitLoading={updateMemberLoading} />

        <div className={styles.splitDrawerBody}>
          <div className="groupLabel" style={{ margin: '16px 0 24px' }}>
            {renderIntlLabel('order_detail_label_rooms_of_new_reserve')}
          </div>
          <Table
            rowKey="id"
            columns={columns}
            dataSource={splitRoomDataSource}
            loading={splitRoomDetailLoading}
            pagination={false}
            scroll={{ x: 1600 }}
            bordered
          />
          <div className="groupLabel" style={{ margin: '40px 0 24px' }}>
            {renderIntlLabel('order_detail_label_bill_info_new_order')}
          </div>
          <Table
            className={styles['bill-table']}
            rowClassName={(record) =>
              [2, 3, 4].includes(record.postType) ? 'reverse-table-cell' : ''
            }
            rowKey="no"
            columns={billColumns}
            dataSource={billList}
            loading={billListLoading}
            rowSelection={{
              ...rowSelection,
            }}
            pagination={false}
            scroll={{ x: 1600 }}
          />
        </div>
      </Drawer>
    </>
  );
};

const mapStateToProps = (state) => {
  const { memberLevelMap } = state.guest;
  return {
    memberLevelMap,
    ...state.order,
  };
};

const ConnectComponent: React.FC<any> = connect(mapStateToProps, {
  getSplitRoomDetailFn: getSplitRoomDetail,
  submitSplitRoomFn: submitSplitRoom,
  initBillListFn: initBillList,
  orderUpdateMemberFn: orderUpdateMember,
})(React.memo(SplitRoom));

export default React.forwardRef<any, any>((props, ref) => (
  <ConnectComponent {...props} myRef={ref} />
));
